import React, { useEffect, useState } from "react";
import cn from "classnames";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Link from "components/link";
import IconButton from "components/icon-button";
import { FormattedMessage } from "react-intl";
import Icon from "components/icon";
import { TableIconCell, CustomTableRow } from "components/table";
import PropTypes from "prop-types";
import { HeaderCell } from "components/table";
import { Restricted, permissions } from "components/auth";
import { DeleteActionWithModal } from "components/buttons";
import mutation from "./mutation";
import { useMutation } from "@apollo/react-hooks";
import { useCurrentCustomerContext } from "components/current-customer-context";
import withStyles from "components/with-styles";
import styles from "./styles/table.css.js";
import { useAuthDataContext } from "components/auth";
import { getUserContracts } from "../../../services/commonService";
import ApiConnectedPaginatedTable from "../../../components/table/paginated-table/api-connected-paginated-table";

const Header = ({ showIcons }) => (
  <TableRow>
    <HeaderCell>
      <FormattedMessage id="common.name" />
    </HeaderCell>
    {showIcons && <TableIconCell />}
  </TableRow>
);

Header.propTypes = {
  /**
   * Muestra o no los iconos de edición, etc
   **/
  showIcons: PropTypes.bool,
};

const RowComponent = ({ item, refetchData, showIcons, onClick, selectedRowId, classes }) => {
  const [deleteContract] = useMutation(mutation);
  const { currentCustomerId } = useCurrentCustomerContext();
  const detailsUrl = `/contract/details/${item._id}`;

  const Row = ({ children }) => {
    return onClick ? (
      <CustomTableRow
        className={cn({ [classes.selectedRow]: selectedRowId === item._id })}
        key={item._id}
        onClick={() => onClick(item)}
      >
        {children}
      </CustomTableRow>
    ) : (
      <CustomTableRow key={item._id} href={detailsUrl}>
        {children}
      </CustomTableRow>
    );
  };

  return (
    <Row>
      <TableCell>{item.name}</TableCell>
      {showIcons && (
        <TableIconCell>
          <Link href={detailsUrl}>
            <IconButton>
              <Icon name="launch" />
            </IconButton>
          </Link>
          <Restricted permissions={[permissions.contract_manage]}>
            <Link href={`/contract/edit/${item._id}`}>
              <IconButton>
                <Icon name="edit" />
              </IconButton>
            </Link>
            <DeleteActionWithModal
              mutation={deleteContract}
              mutationVariables={{
                contractId: item._id,
                customerId: currentCustomerId,
              }}
              elementNameInResult="deleteContract"
              elementToDelete={item.codename}
              refetchDataOnSuccess
              refetchData={refetchData}
            />
          </Restricted>
        </TableIconCell>
      )}
    </Row>
  );
};

RowComponent.propTypes = {
  /**
   ** Elemento a representar en la fila de la tabla
   **/
  item: PropTypes.object,
  /**
   * Función de refetch de datos
   */
  refetchData: PropTypes.func.isRequired,
  /**
   * Muestra o no los iconos de edición, etc
   **/
  showIcons: PropTypes.bool,
  /**
   * Id de la fila seleccionada
   **/
  selectedRowId: PropTypes.string,
  /**
   * Callback opcional de click en la fila
   **/
  onClick: PropTypes.func,
};

const StyledRowComponent = withStyles(styles)(RowComponent);

const ContractPaginatedTable = ({ onClickRow, selectedRowId, contractName, deploymentId }) => {
  const { user } = useAuthDataContext();

  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(5);

  const handleSetPage = value => {
    setPage(value);
  };

  const handleSetPerPage = value => {
    setPerPage(value);
  };

  const [elements, setElements] = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(null);

  const callback = response => {
    setLoading(false);
    setElements(response.data);
    setTotal(parseInt(response.headers["pagination-total-results"]));
  };

  const errorCallback = value => {
    setLoading(false);
    setError(value);
  };

  useEffect(
    () => {
      const variables = { page, perPage, limit: perPage, username: user?.sub, contractName, deploymentId };
      getUserContracts(variables, callback, errorCallback);
    },
    [page, perPage, contractName, deploymentId],
  );

  return (
    <ApiConnectedPaginatedTable
      APIPetition={getUserContracts}
      headerComponent={Header}
      rowComponent={StyledRowComponent}
      paginatedElementsHandler={data => data}
      page={page}
      onChangePage={handleSetPage}
      onChangePerPage={handleSetPerPage}
      rowProps={{ onClick: onClickRow, selectedRowId }}
      elements={elements}
      loading={loading}
      error={error}
      total={total}
      perPage={perPage}
      noDataMessage={"contracts.no_data"}
    />
  );
};

ContractPaginatedTable.propTypes = {
  /**
   ** Objeto que contiene los filtrados de la búsqueda
   **/
  variables: PropTypes.object,
  /**
   * Debouncea o no la recarga de la query de la tabla
   **/
  debounce: PropTypes.bool,
  /**
   * Muestra o no los iconos de edición, etc
   **/
  showIcons: PropTypes.bool,
};

export default ContractPaginatedTable;
